export type StoreItemDiscountType = 'firstPurchase' | 'forMembers' | 'discount';

/**
 * All amounts are rounded to whole CZK for display in the order summary —
 * unlike the exact `…WithVAT` values elsewhere. Never charge with these;
 * Stripe charges the exact unrounded amount.
 */
export type PriceBreakdown = {
  type: 'promoCode' | StoreItemDiscountType;
  percentOff: number | null;
  originalPriceWithVAT: number;
  discountAmountWithVAT: number;
  finalPriceWithVAT: number;
};

// The order summary renders amounts rounded to whole CZK. Rounding the
// original price, the discount and the final price independently of each
// other can make the three displayed numbers disagree by ±1 Kč (e.g.
// 301.4 / 180.84 shows 301, -121 and 181), so all amounts are rounded here
// and the discount is derived from the already-rounded pair. The rounding is
// display-only: Stripe charges the exact unrounded amount.
export function buildPriceBreakdown(
  type: PriceBreakdown['type'],
  originalPriceWithVAT: number,
  finalPriceWithVAT: number,
  percentOff: number | null = null,
): PriceBreakdown {
  const roundedOriginal = Math.round(originalPriceWithVAT);
  const roundedFinal = Math.round(finalPriceWithVAT);
  return {
    type,
    percentOff,
    originalPriceWithVAT: roundedOriginal,
    discountAmountWithVAT: roundedOriginal - roundedFinal,
    finalPriceWithVAT: roundedFinal,
  };
}

export type StoreItemDiscount = {
  type: StoreItemDiscountType;
  originalPriceWithVAT: number;
  finalPriceWithVAT: number;
};

type DiscountPricedItem = {
  priceWithVAT: number;
  firstPriceWithVAT?: number | null;
  priceForMembersWithVAT?: number | null;
  discountPriceWithVAT?: number | null;
};

// Discounts are not combinable: first purchase beats the member price, which
// beats the bundle discount, and only a genuine reduction counts (the API may
// return a discounted price equal to the base price for misconfigured items).
// The API applies the same priority when charging — keep in sync with
// resolveCheckoutPricing (apps/essentee-api/src/app/stripe/checkout.ts).
export function resolveStoreItemDiscount(
  item: DiscountPricedItem,
  hasActiveMembership: boolean,
): StoreItemDiscount | null {
  if (
    item.firstPriceWithVAT != null &&
    item.firstPriceWithVAT < item.priceWithVAT
  ) {
    return {
      type: 'firstPurchase',
      originalPriceWithVAT: item.priceWithVAT,
      finalPriceWithVAT: item.firstPriceWithVAT,
    };
  }

  if (
    hasActiveMembership &&
    item.priceForMembersWithVAT != null &&
    item.priceForMembersWithVAT < item.priceWithVAT
  ) {
    return {
      type: 'forMembers',
      originalPriceWithVAT: item.priceWithVAT,
      finalPriceWithVAT: item.priceForMembersWithVAT,
    };
  }

  if (
    item.discountPriceWithVAT != null &&
    item.discountPriceWithVAT < item.priceWithVAT
  ) {
    return {
      type: 'discount',
      originalPriceWithVAT: item.priceWithVAT,
      finalPriceWithVAT: item.discountPriceWithVAT,
    };
  }

  return null;
}

export type StoreItemPriceView = {
  displayedPriceWithVAT: number | null;
  isFirstPurchasePrice: boolean;
  isMemberPrice: boolean;
  hasDiscount: boolean;
  currentPriceClass: 'current-price red' | 'current-price primary';
};

export function resolveStoreItemPriceView(
  item: DiscountPricedItem | null | undefined,
  hasActiveMembership: boolean,
): StoreItemPriceView {
  const discount =
    item == null ? null : resolveStoreItemDiscount(item, hasActiveMembership);
  return {
    displayedPriceWithVAT:
      discount?.finalPriceWithVAT ?? item?.priceWithVAT ?? null,
    isFirstPurchasePrice: discount?.type === 'firstPurchase',
    isMemberPrice: discount?.type === 'forMembers',
    hasDiscount: discount != null,
    currentPriceClass:
      discount == null ? 'current-price primary' : 'current-price red',
  };
}
